/**
 * 可配置的接口提供者
 * 用于网络请求、文件上传、数据字典等功能的全局配置
 */

import React from "react";
import type { ThemeConfig } from "antd";
import type { ColumnSettingValue } from "../components/AndTable/types";

export interface RequestConfig {
  url?: string;
  baseURL?: string;
  method?: "GET" | "POST" | "PUT" | "DELETE" | "PATCH" | string;
  headers?: Record<string, string>;
  data?: any;
  params?: any;
  timeout?: number;
}

export interface UploadConfig {
  url?: string;
  maxSize?: number;
  accept?: string[];
  headers?: Record<string, string>;
  form?: Record<string, any>;
  query?: Record<string, any>;
}

/**
 * 字典选项接口
 */
export interface DictOption {
  label: string;
  value: string | number;
  disabled?: boolean;
  children?: DictOption[];
  [key: string]: any;
}

/**
 * 字典 value 匹配：数据字典取回的 value 多为 string，实际数据可能为 number，
 * 统一转为 string 后再比较，确保匹配正确。
 */
export function dictValueEquals(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  return String(a ?? '') === String(b ?? '');
}

/**
 * 从字典选项中按 value 查找（使用 dictValueEquals 做类型无关匹配）
 */
export function findDictOptionByValue<T extends { value?: unknown }>(
  options: T[],
  value: unknown
): T | undefined {
  if (!Array.isArray(options)) return undefined;
  return options.find((opt) => dictValueEquals(opt.value, value));
}

/**
 * 全局配置接口
 */
export interface CoreConfig {
  /** 基础 URL */
  baseURL?: string;

  /** 通用请求方法 */
  request?: (config: RequestConfig) => Promise<any>;

  /** 文件上传 */
  upload?: (file: File, config?: UploadConfig) => Promise<any>;

  /** 获取数据字典 */
  getDictOptions?: (dictCode: string) => Promise<DictOption[]>;

  /** 获取认证 Token */
  getAccessToken?: () => string | null;

  /** 组件解析器 - 用于 AndDynamicContainer 动态加载组件 */
  componentResolver?: (name: string) => React.ComponentType<any> | null;

  /**
   * 一级壳层 DynamicContainer keep-alive：切走时 display:none 保留实例，避免整页 remount。
   * 默认仅作用于 sessionId=main 且 storePropsKey=dynamicRoute 的容器。
   */
  keepAliveComponentNames?: string[];
  keepAliveShell?: { sessionId: string; storePropsKey: string };

  /** Ant Design 主题配置 - 用于 UIUtils.showDrawer/showModal 等命令式 API */
  theme?: ThemeConfig;

  /** 资源域名（用于 viewRes 拼接完整访问地址） */
  resDomain?: string;

  /** 将资源路径 src 转为完整访问地址，并附加 auth_token */
  viewRes?: (src: string) => string;

  /**
   * pdf.js CMap 资源目录的 base URL（建议以 `/` 结尾）。
   * 内网部署勿依赖外网 CDN；将 `pdfjs-dist/cmaps` 拷到站点静态目录（如 `/pdfjs/cmaps/`）后在此填写。
   * 未配置时不在线拉取 cmaps，多数拉丁文 PDF 仍可预览；部分中文等编码 PDF 可能缺字。
   */
  pdfCMapUrl?: string;

  /**
   * 未调用 setPdfjsWorkerSrc 时的 worker 脚本地址（如同源静态资源或内网 CDN）。
   * 正常应在应用入口调用 setPdfjsWorkerSrc；此项仅兜底，勿指向需翻墙的地址。
   */
  pdfWorkerFallbackUrl?: string;

  /**
   * AndUpload 点击预览时的全局默认行为（未配置组件 previewAction 时生效）。
   * 由应用层注入，例如 showDrawer + common-view。
   */
  defaultUploadViewAction?: (ctx: UploadPreviewContext) => void;

  /**
   * 保存用户表列显示配置（POST /dms/user-table-column-setting/save）。
   * tableCode 由应用层拼 `${sessionId}_${tableKey}`，避免跨页面碰撞。
   */
  saveUserTableColumnSetting?: (args: {
    sessionId: string;
    tableKey: string;
    tableJson: ColumnSettingValue;
  }) => Promise<unknown>;

  /**
   * 读取用户表列显示配置（GET /dms/user-table-column-setting/get）。
   * 无记录时返回 null。
   */
  loadUserTableColumnSetting?: (args: {
    sessionId: string;
    tableKey: string;
  }) => Promise<ColumnSettingValue | null>;
}

/** AndUpload 预览上下文，供 defaultUploadViewAction / previewAction 使用 */
export interface UploadPreviewContext {
  file: Record<string, any>;
  fileList: Record<string, any>[];
  activeIndex: number;
  previewUrl: string;
  src?: string;
  id?: string;
  sessionId?: string;
  dataSet?: string;
  dataSetField?: string;
  index?: number;
  parentIndex?: number;
}

let globalConfig: CoreConfig = {};

/**
 * 设置全局配置
 */
export function setCoreConfig(config: CoreConfig) {
  globalConfig = { ...globalConfig, ...config };
}

/**
 * 获取全局配置
 */
export function getCoreConfig(): CoreConfig {
  return globalConfig;
}

/**
 * 执行网络请求
 */
export async function request(config: RequestConfig): Promise<any> {
  if (globalConfig.request) {
    // 自动添加 baseURL
    const finalConfig = {
      ...config,
      baseURL: config.baseURL || globalConfig.baseURL,
    };
    return globalConfig.request(finalConfig);
  }
  throw new Error(
    "Request handler not configured. Please call setCoreConfig() first."
  );
}

/**
 * 执行文件上传
 */
export async function upload(file: File, config?: UploadConfig): Promise<any> {
  if (globalConfig.upload) {
    return globalConfig.upload(file, config);
  }
  throw new Error(
    "Upload handler not configured. Please call setCoreConfig() first."
  );
}

/**
 * 获取数据字典
 */
export async function getDictOptions(dictCode: string): Promise<DictOption[]> {
  if (globalConfig.getDictOptions) {
    return globalConfig.getDictOptions(dictCode);
  }
  console.warn(
    "Dict service not configured. Please call setCoreConfig() with getDictOptions."
  );
  return [];
}

/**
 * 获取认证 Token
 */
export function getAccessToken(): string | null {
  if (globalConfig.getAccessToken) {
    return globalConfig.getAccessToken();
  }
  return null;
}

// Convenience export for request
export const req = {
  post: (
    url: string,
    data?: any,
    config?: Omit<RequestConfig, "url" | "data" | "method">
  ) => request({ url, data, method: "POST", ...config }),
  get: (
    url: string,
    params?: any,
    config?: Omit<RequestConfig, "url" | "params" | "method">
  ) => request({ url, params, method: "GET", ...config }),
};
